import fetchToCurl, { FetchOptions } from "fetch-to-curl";
import { GraphQLSingularResponse } from "relay-runtime";
import type { Dispatcher } from "undici";

import { DuetError } from "@framework/relay/errors";
import { log } from "@voxmedia/duet-utils/logging";

import { GraphQLOperation, MiddlewareContext } from "./Middleware";
import {
  getWpApiDispatcher,
  isRetryableConnectError,
  WP_API_CONNECT_RETRIES,
} from "./wp-api-dispatcher";

type Fetcher = (ctx: MiddlewareContext) => Promise<GraphQLSingularResponse>;

/** `dispatcher` is an undici extension that the DOM `RequestInit` omits. */
type FetchInit = RequestInit & { dispatcher?: Dispatcher };

/**
 * Retries the request when the connection was never established. Each attempt
 * opens a fresh socket, and so a fresh source port — the only thing that gets
 * around a black-holed path.
 *
 * `retries` is 0 wherever the dispatcher does not apply. Retrying is only
 * recovery when paired with the dispatcher's lower `connectTimeout`; against
 * undici's 10s default it would just stack a second full timeout onto the
 * first. That matters for the edge runtime, which reaches this fetcher through
 * `createBrowserNetwork` and carries its own bundled undici, so it can raise a
 * genuine `UND_ERR_CONNECT_TIMEOUT` that our dispatcher never governed.
 *
 * Sits below `queryLockMiddleware`, so an attempt that recovers never reaches
 * its circuit breaker.
 */
async function fetchWithConnectRetry(
  ctx: Readonly<MiddlewareContext>,
  fetchOpts: FetchInit,
  retries: number,
): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetch(ctx.url, fetchOpts);
    } catch (err) {
      if (attempt >= retries || !isRetryableConnectError(err)) {
        throw err;
      }

      log.warn(
        {
          url: ctx.url,
          operationName: ctx.operation.operationName,
          attempt: attempt + 1,
          code: (err as { cause?: { code?: string } }).cause?.code,
        },
        "Retrying GraphQL request after connect failure",
      );
    }
  }
}

function createFetcher(
  options: {
    onHeaders?: (headers: Headers, operation: GraphQLOperation) => void;
  } = {},
): Fetcher {
  return async (ctx: Readonly<MiddlewareContext>) => {
    const fetchOpts: FetchInit = {
      ...ctx.fetchOpts,
    };

    const headers: Record<string, string> = {
      ...ctx.fetchOpts.headers,
      Accept: "application/json",
    };

    if (ctx.fetchOpts.method === "POST") {
      fetchOpts.body = JSON.stringify(ctx.operation);
      headers["Content-Type"] = "application/json";
    }

    // Merge new headers into existing headers.
    fetchOpts.headers = { ...fetchOpts.headers, ...headers };

    // Undefined off the Node runtime, and if undici could not be loaded, where
    // `fetch` is left exactly as it behaves today.
    const dispatcher = await getWpApiDispatcher();

    if (dispatcher) {
      fetchOpts.dispatcher = dispatcher;
    }

    /**
     * log.debug won't log unless debug is enabled but the function will still be called
     * so the whole log command is wrapped in the condition to avoid function execution
     */
    if (process.env.DEBUG_ENABLED === "true") {
      log.debug(
        `GQL Curl Command for ${ctx.operation.operationName}: \n\n${fetchToCurl(ctx.url, fetchOpts as FetchOptions)}\n`,
      );
    }

    const res = await fetchWithConnectRetry(
      ctx,
      fetchOpts,
      dispatcher ? WP_API_CONNECT_RETRIES : 0,
    );
    const text = await res.text();

    if (!res.ok) {
      throw new DuetError(
        `[${res.status}] GraphQL Request Failed: ${res.statusText}`,
        {
          cause: `[${res.status}] GraphQL request failure due to ${res.statusText}`,
          extra: {
            status: res.status,
            statusText: res.statusText,
            url: ctx.url,
            operationName: ctx.operation.operationName,
            text,
            requestMethod: fetchOpts.method || "GET",
            // Include response headers for debugging (e.g., Fastly headers)
            responseHeaders: (() => {
              const headers: Record<string, string> = {};
              res.headers.forEach((value, key) => {
                headers[key] = value;
              });
              return headers;
            })(),
          },
        },
      );
    }

    let response: GraphQLSingularResponse;
    try {
      response = JSON.parse(text) as GraphQLSingularResponse;
    } catch (err) {
      throw new DuetError(
        `Failed to parse ${ctx.operation.operationName} GraphQL response JSON`,
        {
          cause: err,
          extra: {
            status: res.status,
            statusText: res.statusText,
            url: ctx.url,
            operationName: ctx.operation.operationName,
            text,
            requestMethod: fetchOpts.method || "GET",
            responseHeaders: (() => {
              const headers: Record<string, string> = {};
              res.headers.forEach((value, key) => {
                headers[key] = value;
              });
              return headers;
            })(),
          },
        },
      );
    }

    // Call headers callback
    if (options.onHeaders) {
      options.onHeaders(res.headers, ctx.operation);
    }

    return response;
  };
}

export default createFetcher;
